import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom' 
import DOMPurify from 'dompurify' // CRITICAL: Added for structural sanitization layer

import RiverBackground from './RiverBackground.jsx'
import TopBar from './TopBar.jsx'
import SplashScreen from './SplashScreen.jsx'
import Onboarding from './Onboarding.jsx'
import TrailerModal from './TrailerModal.jsx'
import FeedbackModal from './FeedbackModal.jsx'
import SettingsFeedback from './SettingsFeedback.jsx'
import SettingsFoundersClub from './SettingsFoundersClub.jsx'
import WaitlistSection from './WaitlistSection.jsx'
import Card from './Card.jsx'
import Badge from './Badge.jsx'
import VpnBanner from './VpnBanner.jsx'
import AddToHomeScreen from './AddToHomeScreen.jsx'
import { SERVICES, MOODS, HAS_AFFILIATES, REGIONS } from '../constants.js'
import { track } from '../analytics.js'
import { loadServices, saveServices, loadPrefs, savePrefs, clearPrefs, loadRegion, saveRegion, loadSetupDone, incrementVisitCount } from '../storage.js'
import { getRecommendations, getTrending, searchActor } from '../api.js'
import flowIcon from '@assets/image_1777447102475.png'

const LOAD_PHRASES = [
  'Consulting the maps…',
  'Clearing the streaming wilderness…',
  'Summoning tonight\'s chapter…',
  'Reading between the streams…',
  'Charting your perfect evening…',
  'Leafing through the archives…',
  'Gathering the finest picks…',
  'Once upon a search…',
  'The guide is thinking…',
  'Scanning all your streams…',
]

export default function Flow() {
  // ── ROUTING HOOKS ──
  const navigate = useNavigate()
  const { id } = useParams() 
  const [searchParams, setSearchParams] = useSearchParams()

  // ── INCREMENT VISIT COUNT ONCE ON MOUNT ──
  useEffect(() => { incrementVisitCount() }, [])

  // ── STATE MANAGEMENT ──
  const [showSplash, setShowSplash]     = useState(true)
  const [setupDone, setSetupDone]       = useState(loadSetupDone)
  const [tab, setTab]                   = useState('home')
  const [showFeedback, setShowFeedback] = useState(false)
  const [activeSvc, setActiveSvc]       = useState(loadServices)
  const [query, setQuery]               = useState('')
  const [results, setResults]           = useState(null)
  const [loading, setLoading]           = useState(false)
  const [loadMsg, setLoadMsg]           = useState('')
  const [error, setError]               = useState(null)
  const [prefs, setPrefs]               = useState(loadPrefs)
  const [bgMode, setBgMode]             = useState('dark') 

  const [trailerRec, setTrailerRec]     = useState(null) 
  const [lastQ, setLastQ]               = useState('')
  const [trending, setTrending]         = useState(null)
  const [region, setRegion]             = useState(loadRegion)

  const searchCycleRef  = useRef(0)
  const phraseCycleRef  = useRef(null)
  const [searchFocused, setSearchFocused] = useState(false)
  const [searchMode, setSearchMode]       = useState('movies')
  const [actorProfile, setActorProfile]   = useState(null)

  useEffect(() => {
    if (loading) {
      let i = 0
      setLoadMsg(LOAD_PHRASES[0])
      phraseCycleRef.current = setInterval(() => {
        i = (i + 1) % LOAD_PHRASES.length
        setLoadMsg(LOAD_PHRASES[i])
      }, 2200)
      const handleUnload = () => track('Search_Loading_Abandoned', {
        query:   lastQ,
        trigger: 'browser_exit',
      })
      window.addEventListener('beforeunload', handleUnload)
      return () => {
        clearInterval(phraseCycleRef.current)
        window.removeEventListener('beforeunload', handleUnload)
      }
    }
    clearInterval(phraseCycleRef.current)
    return () => clearInterval(phraseCycleRef.current)
  }, [loading])

  // ── URL-SYNCED MOOD STATE (HARDENED INPUT PARSING) ──
  const selMoods = useMemo(() => {
    const moodsParam = searchParams.get('moods')
    if (!moodsParam) return []

    // Explicit Type & Sanitization Enforcement
    const sanitizedMoodsParam = DOMPurify.sanitize(moodsParam)
    const ids = sanitizedMoodsParam.split(',').map(id => id.trim())
    return MOODS.filter(m => ids.includes(m.id))
  }, [searchParams])

  const setSelMoods = (nextMoods) => {
    if (nextMoods.length > 0) {
      const ids = nextMoods.map(m => m.id).join(',')
      setSearchParams({ moods: ids })
    } else {
      setSearchParams({})
    }
  }

  // ── TITLE PATH EXTRACTORS ──
  const openTitleModal = (rec) => {
    if (!rec?.title) return
    setTrailerRec(rec)
    const currentParams = searchParams.toString()
    const cleanId = encodeURIComponent(rec.title)
    navigate(`/title/${cleanId}${currentParams ? `?${currentParams}` : ''}`)
  }

  const closeTitleModal = () => {
    setTrailerRec(null)
    const currentParams = searchParams.toString()
    navigate(`/${currentParams ? `?${currentParams}` : ''}`)
  }

  // ── ROUTE PARAM SYNC HOOK (DEFENSIVE FIX: ANTI-XSS WRAPPER) ──
  useEffect(() => {
    if (id && !trailerRec) {
      const decodedTitle = decodeURIComponent(id)
      // Strip html/scripts/js-payload expressions out before state execution
      const sanitizedTitle = DOMPurify.sanitize(decodedTitle, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })

      // Fail-closed invariant if route yields a malicious or hollow structure
      if (!sanitizedTitle || sanitizedTitle.trim() === "") {
        navigate('/', { replace: true })
        return
      }

      setTrailerRec({ title: sanitizedTitle })
      if (tab !== 'search' && tab !== 'home') {
        setTab('home')
      }
    } else if (!id && trailerRec) {
      setTrailerRec(null)
    }
  }, [id, navigate, tab, trailerRec])

  useEffect(() => {
    if (selMoods.length > 0 && !results && !loading) {
      doSearch()
    }
  }, [searchParams])

  // ── CORE LOGIC METHODS ──
  const handleOnboardingComplete = (services, regionCode) => {
    // Array type verification before persistence handshake
    if (Array.isArray(services) && typeof regionCode === 'string') {
      setActiveSvc(services)
      setRegion(regionCode)
      setSetupDone(true)
    }
  }

  useEffect(() => {
    let alive = true
    setTrending(null)
    getTrending(region)
      .then(d => { if (alive) setTrending(d.results || []) })
      .catch(() => { if (alive) setTrending([]) })
    return () => { alive = false }
  }, [region])

  const changeRegion = (code) => {
    const sanitizedCode = DOMPurify.sanitize(code).trim()
    setRegion(sanitizedCode)
    saveRegion(sanitizedCode)
  }

  const toggleSvc = (id) => {
    const next = activeSvc.includes(id) ? activeSvc.filter(s => s !== id) : [...activeSvc, id]
    setActiveSvc(next)
    saveServices(next)
  }

  const toggleMood = (m) => {
    setSelMoods(selMoods.find(x => x.id === m.id) ? selMoods.filter(x => x.id !== m.id) : [...selMoods, m])
  }

  const saveRate = (rec, type) => {
    if (!rec?.title || !['like', 'watched'].includes(type)) return

    // Mitigate local cache buffer injection/pollution
    const sanitizedTitle = DOMPurify.sanitize(rec.title)
    const next = [...prefs.filter(p => p.title !== sanitizedTitle), {
      title: sanitizedTitle, genres: rec.genres || [], service: rec.service, type,
    }]
    setPrefs(next)
    savePrefs(next)
  }

  // ── TRANSPORT LAYER REFACTOR (DEFENSIVE FIX: CONCURRENCY LOCK & SANITIZATION) ──
  const doSearch = useCallback(async (override) => {
    const rawQ = override !== undefined ? override
      : selMoods.length > 1 ? selMoods.map(m => m.query).join(' blended with ')
      : selMoods.length === 1 ? selMoods[0].query
      : query

    if (!rawQ?.trim() || loading || activeSvc.length === 0) return

    // Mutate state-cycle tracking immediately before entering the async event horizon
    searchCycleRef.current += 1
    const currentCycle = searchCycleRef.current

    setLoading(true)
    setResults(null)
    setActorProfile(null)
    setError(null)

    // Explicitly neutralize malicious strings or control character chains from the input fields
    const sanitizedQ = DOMPurify.sanitize(rawQ)
    setLastQ(sanitizedQ)
    setTab('search')

    try {
      const { results: recs } = await getRecommendations({ 
        query: sanitizedQ, 
        activeServices: activeSvc, 
        prefs, 
        region 
      })

      // Strict Thread-Context Matching Check
      if (currentCycle === searchCycleRef.current) {
        setResults(recs)
      }
    } catch (e) {
      console.error("[Flow-Sec] Supressed internal tracking stacktrace:", e.message)
      if (currentCycle === searchCycleRef.current) {
        setError('Couldn\'t load recommendations — please try again.')
      }
    } finally {
      if (currentCycle === searchCycleRef.current) {
        setLoading(false)
        setLoadMsg('')
      }
    }
  }, [query, activeSvc, prefs, selMoods, region, loading])

  const doActorSearch = useCallback(async (override) => {
    const rawQ = override !== undefined ? override : query
    if (!rawQ?.trim() || loading) return

    searchCycleRef.current += 1
    const currentCycle = searchCycleRef.current

    setLoading(true)
    setResults(null)
    setActorProfile(null)
    setError(null)

    const sanitizedQ = DOMPurify.sanitize(rawQ)
    setLastQ(sanitizedQ)
    setTab('search')

    try {
      const { person, movies } = await searchActor({
        name:           sanitizedQ,
        activeServices: activeSvc,
        region,
      })
      if (currentCycle === searchCycleRef.current) {
        setActorProfile(person)
        setResults(movies)
      }
    } catch (e) {
      if (currentCycle === searchCycleRef.current) {
        setError('Couldn\'t find that actor — please try again.')
      }
    } finally {
      if (currentCycle === searchCycleRef.current) {
        setLoading(false)
        setLoadMsg('')
      }
    }
  }, [query, activeSvc, region, loading])

  const doSmartSearch = useCallback(() => {
    const trimmed = query.trim()
    if (!trimmed) return
    const words = trimmed.split(/\s+/)
    const looksLikeName = words.length >= 2 && words.length <= 3
      && words.every(w => /^[A-Za-zÀ-ÿ''-]+$/.test(w))
      && trimmed.length < 40
    if (looksLikeName) {
      doActorSearch()
    } else {
      doSearch()
    }
  }, [query, doSearch, doActorSearch])

  const moreLike = (rec) => {
    if (!rec?.title) return
    const sanitizedTitle = DOMPurify.sanitize(rec.title)
    const q = `More titles exactly like "${sanitizedTitle}" — same vibe, tone and genre but completely different shows`
    setQuery(q)
    doSearch(q)
  }


  return (
    <div style={{
      minHeight: '100vh',
      background: bgMode === 'dark' ? '#030a04' : '#f2f8f4',
      color: bgMode === 'dark' ? '#e8f0ea' : '#1e2922',
      fontFamily: "'Sora','SF Pro Text','Helvetica Neue',sans-serif",
      paddingTop: 'calc(60px + env(safe-area-inset-top, 0px))',
      paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
      overflowX: 'hidden',
      position: 'relative',
      transition: 'background 1.2s cubic-bezier(0.25, 1, 0.5, 1), color 0.5s ease',
    }}>
      <RiverBackground mode={bgMode} />
      {showSplash && <SplashScreen onDone={() => setShowSplash(false)} />}
      {!showSplash && !setupDone && <Onboarding onComplete={handleOnboardingComplete} />}
      {!showSplash && (
        <TopBar
          tab={tab}
          onChange={(newTab) => {
            if (loading && newTab !== 'search') {
              track('Search_Loading_Abandoned', {
                query:            lastQ,
                trigger:          'tab_switch',
                abandoned_to_tab: newTab,
              })
            }
            setTab(newTab)
          }}
          activeMoods={selMoods}
          onClearMoods={() => setSelMoods([])}
        />
      )}
      {trailerRec && <TrailerModal rec={trailerRec} onClose={closeTitleModal} />}

      <div style={{ visibility: showSplash ? 'hidden' : 'visible' }}>

        {/* ── HOME ── */}
        {tab === 'home' && (
          <div style={{ position: 'relative', zIndex: 1, animation: 'fadeUp 0.4s ease' }}>
            <div style={{ padding: '52px 20px 44px', textAlign: 'center', borderBottom: '1px solid rgba(80,160,200,0.08)' }}>
              <img
                src={flowIcon}
                alt="Flow"
                width="96"
                height="96"
                style={{
                  width: 96, height: 96, borderRadius: 22,
                  display: 'block', margin: '0 auto 22px',
                  boxShadow: '0 12px 48px rgba(60,150,200,0.35), 0 0 100px rgba(40,110,160,0.2)',
                  animation: 'logoFloat 4s ease-in-out infinite',
                }}
              />
              <h1 style={{
                fontFamily: "'DM Serif Display', Georgia, serif",
                fontSize: 'clamp(28px, 4vw, 50px)',
                fontWeight: 400, fontStyle: 'italic',
                color: bgMode === 'dark' ? 'rgba(220,238,248,0.92)' : '#0f1712',
                letterSpacing: '-0.01em',
                lineHeight: 1.2, marginBottom: 28,
                maxWidth: 440, marginLeft: 'auto', marginRight: 'auto',
              }}>
                All your streams in one place
              </h1>
              <div style={{
                display: 'flex', maxWidth: 480, margin: '0 auto',
                background: bgMode === 'dark' ? 'rgba(4,14,7,0.85)' : 'rgba(255,255,255,0.9)',
                border: searchFocused
                  ? '1.5px solid rgba(34,197,94,0.55)'
                  : (bgMode === 'dark' ? '1.5px solid rgba(34,197,94,0.2)' : '1.5px solid rgba(34,197,94,0.3)'),
                borderRadius: 16, overflow: 'hidden', backdropFilter: 'blur(12px)',
                boxShadow: searchFocused ? '0 0 0 3px rgba(34,197,94,0.1), 0 6px 24px rgba(34,197,94,0.07)' : 'none',
                transition: 'border-color 0.2s ease, box-shadow 0.25s ease',
              }}>
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && doSmartSearch()}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  placeholder='Movies, shows, or actors…'
                  style={{
                    flex: 1, background: 'transparent', border: 'none', outline: 'none',
                    color: bgMode === 'dark' ? '#c8e6cb' : '#1e2922', fontSize: 15, padding: '16px 18px', fontFamily: 'inherit',
                  }}
                />
                <button onClick={doSmartSearch} style={{
                  background: 'linear-gradient(135deg,#16a34a,#15803d)',
                  border: 'none', padding: '0 22px', color: '#fff',
                  fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                  minWidth: 72, minHeight: 52, WebkitTapHighlightColor: 'transparent',
                  transition: 'opacity 0.15s ease',
                }}>Go</button>
              </div>

              <div style={{ marginTop: 28, padding: '0 4px' }}>
                <WaitlistSection bgMode={bgMode} />
                <AddToHomeScreen bgMode={bgMode} />
              </div>
            </div>

            {/* Quick moods */}
            <div style={{ padding: '24px 20px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 22, height: 1, background: bgMode === 'dark' ? 'rgba(180,215,235,0.2)' : 'rgba(30,80,50,0.18)', flexShrink: 0 }} />
                <span style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontStyle: 'italic', fontSize: 15, color: bgMode === 'dark' ? 'rgba(200,228,242,0.65)' : 'rgba(30,80,50,0.6)', letterSpacing: '0.005em' }}>Quick picks</span>
              </div>
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, WebkitOverflowScrolling: 'touch' }}>
                {MOODS.map(m => (
                  <button key={m.id} onClick={() => { setSelMoods([m]); doSearch(m.query) }} style={{
                    flexShrink: 0, background: bgMode === 'dark' ? 'rgba(4,14,7,0.82)' : 'rgba(255,255,255,0.8)',
                    border: bgMode === 'dark' ? '1.5px solid rgba(34,197,94,0.14)' : '1.5px solid rgba(34,197,94,0.22)', 
                    borderRadius: 50, padding: '9px 16px', color: bgMode === 'dark' ? 'rgba(140,200,150,0.8)' : '#15803d',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit',
                    minHeight: 42, backdropFilter: 'blur(8px)',
                    WebkitTapHighlightColor: 'transparent',
                  }}>{m.icon} {m.label}</button>
                ))}
              </div>
            </div>

            {trending && trending.length > 0 && (
              <div style={{ padding: '20px 16px 4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px', marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 22, height: 1, background: bgMode === 'dark' ? 'rgba(180,215,235,0.2)' : 'rgba(30,80,50,0.18)', flexShrink: 0 }} />
                    <span style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontStyle: 'italic', fontSize: 15, color: bgMode === 'dark' ? 'rgba(200,228,242,0.65)' : 'rgba(30,80,50,0.6)' }}>Trending on Prime &amp; Apple TV+</span>
                  </div>
                  {HAS_AFFILIATES && (
                    <div style={{ fontSize: 9, color: 'rgba(180,215,235,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      Affiliate
                    </div>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 14 }}>
                  {trending.map((rec, i) => (
                    <Card key={`trend-${rec.tmdbId || rec.title}-${i}`} rec={rec} idx={i}
                      prefs={prefs} onRate={saveRate}
                      onMoreLike={moreLike} onTrailer={openTitleModal}
                      bgMode={bgMode}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── SEARCH / RESULTS ── */}
        {tab === 'search' && (
          <div style={{ position: 'relative', zIndex: 1, animation: 'fadeUp 0.3s ease' }}>
            <div style={{
              background: bgMode === 'dark' ? 'rgba(3,10,4,0.6)' : 'rgba(255,255,255,0.4)',
              borderBottom: '1px solid rgba(34,197,94,0.08)',
              padding: '12px 16px',
            }}>
              <div style={{
                display: 'flex', gap: 0, alignItems: 'center',
                background: bgMode === 'dark' ? 'rgba(4,14,7,0.9)' : 'rgba(255,255,255,0.9)',
                border: bgMode === 'dark' ? '1.5px solid rgba(34,197,94,0.18)' : '1.5px solid rgba(34,197,94,0.25)',
                borderRadius: 14, height: 48, backdropFilter: 'blur(12px)', overflow: 'hidden',
              }}>
                <span style={{ color: 'rgba(180,215,235,0.6)', fontSize: 15, paddingLeft: 14, flexShrink: 0 }}>🔍</span>
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && doSmartSearch()}
                  placeholder='Movies, shows, or actors…'
                  style={{
                    flex: 1, background: 'transparent', border: 'none', outline: 'none',
                    color: bgMode === 'dark' ? '#c8e6cb' : '#1e2922', fontSize: 15, fontFamily: 'inherit',
                    padding: '0 10px',
                  }}
                />
                <button
                  onClick={doSmartSearch}
                  style={{
                    height: '100%', paddingLeft: 18, paddingRight: 18, flexShrink: 0,
                    background: 'linear-gradient(135deg, #16a34a, #15803d)',
                    border: 'none', color: '#fff',
                    fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                    WebkitTapHighlightColor: 'transparent',
                    transition: 'opacity 0.15s ease',
                  }}
                >Go</button>
              </div>

              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase',
                    color: bgMode === 'dark' ? 'rgba(180,215,235,0.4)' : 'rgba(30,80,50,0.4)',
                  }}>Moods</span>
                  <div style={{ flex: 1, height: 1, background: bgMode === 'dark' ? 'rgba(180,215,235,0.08)' : 'rgba(30,80,50,0.1)' }} />
                </div>
                <div style={{ display: 'flex', gap: 7, overflowX: 'auto', paddingBottom: 2, WebkitOverflowScrolling: 'touch' }}>
                  {MOODS.map(m => {
                    const sel = selMoods.find(x => x.id === m.id)
                    return (
                      <button key={m.id} onClick={() => toggleMood(m)} style={{
                        flexShrink: 0,
                        background: sel ? 'rgba(22,163,74,0.2)' : (bgMode === 'dark' ? 'rgba(4,14,7,0.8)' : 'rgba(255,255,255,0.8)'),
                        border: `1.5px solid ${sel ? 'rgba(34,197,94,0.5)' : 'rgba(34,197,94,0.1)'}`,
                        borderRadius: 50, padding: '7px 13px',
                        color: sel ? '#86efac' : (bgMode === 'dark' ? 'rgba(80,140,90,0.8)' : '#15803d'),
                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        minHeight: 36, fontFamily: 'inherit',
                        WebkitTapHighlightColor: 'transparent',
                      }}>{m.icon} {m.label}</button>
                    )
                  })}
                </div>

                <button
                  onClick={() => selMoods.length > 0 && doSearch()}
                  style={{
                    marginTop: 10, width: '100%',
                    background: selMoods.length > 0
                      ? 'linear-gradient(135deg,#16a34a,#15803d)'
                      : (bgMode === 'dark' ? 'rgba(4,14,7,0.6)' : 'rgba(240,248,242,0.8)'),
                    border: selMoods.length > 0
                      ? 'none'
                      : `1.5px dashed ${bgMode === 'dark' ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.2)'}`,
                    borderRadius: 12, padding: '11px 16px',
                    color: selMoods.length > 0
                      ? '#fff'
                      : (bgMode === 'dark' ? 'rgba(140,200,150,0.35)' : 'rgba(30,80,50,0.3)'),
                    fontSize: 13, fontWeight: 700, cursor: selMoods.length > 0 ? 'pointer' : 'default',
                    fontFamily: 'inherit', textAlign: 'center', lineHeight: 1.3,
                    transition: 'all 0.2s ease',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {selMoods.length > 1
                    ? `✦ Blend ${selMoods.length} moods →`
                    : selMoods.length === 1
                      ? `✦ Find my ${selMoods[0].label.toLowerCase()} stream →`
                      : 'Pick moods above to search'}
                </button>
              </div>
            </div>

            {loading && (
              <div style={{ padding: '72px 24px 60px', textAlign: 'center', animation: 'fadeUp 0.35s ease' }}>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 28 }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{
                      width: 7, height: 7, borderRadius: '50%',
                      background: bgMode === 'dark' ? 'rgba(34,197,94,0.55)' : 'rgba(22,163,74,0.55)',
                      animation: `loadDot 1.4s ease-in-out ${i * 0.22}s infinite`,
                    }} />
                  ))}
                </div>
                <p key={loadMsg} style={{
                  fontFamily: "'DM Serif Display', Georgia, serif",
                  fontStyle: 'italic',
                  fontSize: 'clamp(18px, 3vw, 26px)',
                  color: bgMode === 'dark' ? 'rgba(220,238,248,0.72)' : 'rgba(30,80,50,0.65)',
                  lineHeight: 1.45, letterSpacing: '-0.01em',
                  maxWidth: 300, margin: '0 auto',
                  animation: 'phraseIn 0.42s ease',
                }}>
                  {loadMsg}
                </p>
              </div>
            )}

            {error && !loading && (
              <div style={{ padding: '56px 24px', textAlign: 'center', animation: 'fadeUp 0.3s ease' }}>
                <p style={{
                  fontFamily: "'DM Serif Display', Georgia, serif",
                  fontStyle: 'italic', fontSize: 17,
                  color: bgMode === 'dark' ? 'rgba(220,238,248,0.55)' : 'rgba(30,80,50,0.5)',
                  marginBottom: 18, lineHeight: 1.5,
                }}>The stream ran dry. Try again?</p>
                <button onClick={() => doSearch()} style={{
                  background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)',
                  color: '#16a34a', borderRadius: 10, padding: '10px 22px',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}>Try again</button>
              </div>
            )}

            {results && !loading && (
              <div style={{ padding: '20px 16px 28px' }}>
                {actorProfile && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    background: bgMode === 'dark' ? 'rgba(4,14,7,0.75)' : 'rgba(255,255,255,0.88)',
                    border: '1px solid rgba(34,197,94,0.15)', borderRadius: 16,
                    padding: '14px 16px', marginBottom: 18, backdropFilter: 'blur(12px)',
                  }}>
                    {actorProfile.profile ? (
                      <img src={actorProfile.profile} alt={actorProfile.name}
                        style={{ width: 62, height: 62, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                    ) : (
                      <div style={{
                        width: 62, height: 62, borderRadius: '50%',
                        background: 'rgba(34,197,94,0.1)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0,
                      }}>🎭</div>
                    )}
                    <div>
                      <div style={{ fontSize: 17, fontWeight: 700, color: bgMode === 'dark' ? '#c8e6cb' : '#1e2922', lineHeight: 1.2 }}>
                        {actorProfile.name}
                      </div>
                      <div style={{ fontSize: 12, color: bgMode === 'dark' ? 'rgba(200,228,242,0.55)' : 'rgba(30,80,50,0.55)', marginTop: 3 }}>
                        {actorProfile.known_for_department || 'Actor'}
                      </div>
                      <div style={{ fontSize: 12, color: bgMode === 'dark' ? 'rgba(140,200,150,0.75)' : '#15803d', marginTop: 4, fontWeight: 600 }}>
                        {results?.length || 0} titles found
                      </div>
                    </div>
                  </div>
                )}
                {lastQ && !actorProfile && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <div style={{ width: 22, height: 1, background: bgMode === 'dark' ? 'rgba(180,215,235,0.2)' : 'rgba(30,80,50,0.18)', flexShrink: 0 }} />
                    <span style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontStyle: 'italic', fontSize: 15, color: bgMode === 'dark' ? 'rgba(200,228,242,0.65)' : 'rgba(30,80,50,0.6)' }}>
                      Results for &ldquo;{lastQ}&rdquo;
                    </span>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 14 }}>
                  {results.map((rec, i) => (
                    <Card key={`${rec.title}-${i}`} rec={rec} idx={i}
                      prefs={prefs} onRate={saveRate}
                      onMoreLike={moreLike} onTrailer={openTitleModal}
                      bgMode={bgMode}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── MOODS ── */}
        {tab === 'moods' && (
          <div style={{ position: 'relative', zIndex: 1, padding: '28px 16px', animation: 'fadeUp 0.3s ease' }}>
            <h2 style={{
              fontFamily: "'DM Serif Display', Georgia, serif",
              fontStyle: 'italic', fontWeight: 400,
              fontSize: 'clamp(24px, 3.5vw, 38px)',
              color: bgMode === 'dark' ? 'rgba(220,238,248,0.88)' : '#0f1712',
              marginBottom: 6, lineHeight: 1.2,
            }}>Set the mood.</h2>
            <p style={{
              fontSize: 13,
              color: bgMode === 'dark' ? 'rgba(160,205,235,0.5)' : 'rgba(30,80,50,0.55)',
              marginBottom: 20, lineHeight: 1.5,
            }}>Pick one or several — Flow blends them.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 20 }}>
              {MOODS.map(m => {
                const sel = selMoods.find(x => x.id === m.id)
                return (
                  <button key={m.id} onClick={() => toggleMood(m)} style={{
                    background: sel ? 'rgba(22,163,74,0.18)' : (bgMode === 'dark' ? 'rgba(4,14,7,0.82)' : 'rgba(255,255,255,0.85)'),
                    border: `1.5px solid ${sel ? 'rgba(34,197,94,0.5)' : 'rgba(34,197,94,0.1)'}`,
                    borderRadius: 16, padding: '17px 14px', cursor: 'pointer',
                    minHeight: 80, backdropFilter: 'blur(12px)',
                  }}>
                    <div style={{ fontSize: 26, marginBottom: 7 }}>{m.icon}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: sel ? '#86efac' : (bgMode === 'dark' ? 'rgba(100,160,110,0.9)' : '#15803d') }}>{m.label}</div>
                  </button>
                )
              })}
            </div>

            <button
              onClick={() => { setTab('search'); doSearch() }}
              disabled={selMoods.length === 0}
              style={{
                width: '100%', padding: '15px 0',
                background: selMoods.length > 0
                  ? 'linear-gradient(135deg, #16a34a, #15803d)'
                  : (bgMode === 'dark' ? 'rgba(4,14,7,0.6)' : 'rgba(0,0,0,0.06)'),
                border: selMoods.length > 0
                  ? 'none'
                  : `1.5px solid ${bgMode === 'dark' ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.18)'}`,
                borderRadius: 14,
                color: selMoods.length > 0
                  ? '#fff'
                  : (bgMode === 'dark' ? 'rgba(100,160,110,0.4)' : 'rgba(30,80,50,0.35)'),
                fontSize: 15, fontWeight: 700, cursor: selMoods.length > 0 ? 'pointer' : 'default',
                fontFamily: 'inherit', letterSpacing: '0.01em',
                WebkitTapHighlightColor: 'transparent',
                transition: 'all 0.2s ease',
                boxShadow: selMoods.length > 0 ? '0 4px 18px rgba(22,163,74,0.35)' : 'none',
              }}
            >
              {selMoods.length > 0 ? `Find my stream →` : 'Select a mood to search'}
            </button>
          </div>
        )}

        {/* ── SETTINGS ── */}
        {tab === 'settings' && (
          <div style={{ position: 'relative', zIndex: 1, padding: '28px 16px', animation: 'fadeUp 0.3s ease', maxWidth: 520, margin: '0 auto' }}>
            <h2 style={{
              fontFamily: "'DM Serif Display', Georgia, serif",
              fontStyle: 'italic', fontWeight: 400,
              fontSize: 'clamp(22px, 3vw, 32px)',
              color: bgMode === 'dark' ? 'rgba(220,238,248,0.92)' : '#0f1712',
              marginBottom: 20, lineHeight: 1.25,
            }}>Your Streams &amp; Preferences</h2>

            {/* Founder's Club — placed prominently at the top of Settings */}
            <SettingsFoundersClub bgMode={bgMode} />

            {/* Feedback — sits directly below Founder's Club */}
            <SettingsFeedback bgMode={bgMode} />

            {/* Theme Mode Toggle Panel */}
            <div style={{ 
              background: bgMode === 'dark' ? 'rgba(4,14,7,0.82)' : 'rgba(255,255,255,0.75)', 
              border: bgMode === 'dark' ? '1.5px solid rgba(34,197,94,0.14)' : '1.5px solid rgba(34,197,94,0.22)', 
              borderRadius: 16, padding: 18, marginBottom: 16, backdropFilter: 'blur(12px)',
              transition: 'all 0.5s ease'
            }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: bgMode === 'dark' ? 'rgba(180,215,235,0.65)' : 'rgba(30,50,40,0.65)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
                Interface Vibe
              </label>
              <div style={{ display: 'flex', background: bgMode === 'dark' ? 'rgba(3,10,4,0.6)' : 'rgba(0,0,0,0.05)', borderRadius: 10, padding: 4 }}>
                <button 
                  onClick={() => setBgMode('dark')}
                  style={{
                    flex: 1, border: 'none', borderRadius: 8, padding: '10px 0', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
                    background: bgMode === 'dark' ? 'linear-gradient(135deg,#16a34a,#15803d)' : 'transparent',
                    color: bgMode === 'dark' ? '#fff' : 'rgba(30,50,40,0.5)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  🌙 Deep Stream (Dark)
                </button>
                <button 
                  onClick={() => setBgMode('light')}
                  style={{
                    flex: 1, border: 'none', borderRadius: 8, padding: '10px 0', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
                    background: bgMode === 'light' ? 'linear-gradient(135deg,#16a34a,#15803d)' : 'transparent',
                    color: bgMode === 'light' ? '#fff' : 'rgba(30,50,40,0.6)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  ☀️ Fresh Mint (Light)
                </button>
              </div>
            </div>

            {/* Region Selector */}
            <div style={{ 
              background: bgMode === 'dark' ? 'rgba(4,14,7,0.82)' : 'rgba(255,255,255,0.75)', 
              border: bgMode === 'dark' ? '1.5px solid rgba(34,197,94,0.14)' : '1.5px solid rgba(34,197,94,0.22)', 
              borderRadius: 16, padding: 18, marginBottom: 16, backdropFilter: 'blur(12px)' 
            }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: bgMode === 'dark' ? 'rgba(180,215,235,0.65)' : 'rgba(30,50,40,0.65)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Streaming Region</label>
              <select value={region} onChange={e => changeRegion(e.target.value)} style={{ width: '100%', background: bgMode === 'dark' ? '#040e07' : '#ffffff', color: bgMode === 'dark' ? '#c8e6cb' : '#1e2922', border: '1.5px solid rgba(34,197,94,0.3)', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }}>
                {REGIONS.map(r => <option key={r.code} value={r.code}>{r.flag} {r.name}</option>)}
              </select>
            </div>

            {/* Connected Services Grid */}
            <div style={{ 
              background: bgMode === 'dark' ? 'rgba(4,14,7,0.82)' : 'rgba(255,255,255,0.75)', 
              border: bgMode === 'dark' ? '1.5px solid rgba(34,197,94,0.14)' : '1.5px solid rgba(34,197,94,0.22)', 
              borderRadius: 16, padding: 18, backdropFilter: 'blur(12px)' 
            }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: bgMode === 'dark' ? 'rgba(180,215,235,0.65)' : 'rgba(30,50,40,0.65)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Enabled Platforms</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                {SERVICES.map(s => {
                  const active = activeSvc.includes(s.id);
                  return (
                    <button key={s.id} onClick={() => toggleSvc(s.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, background: active ? (bgMode === 'dark' ? 'rgba(22,163,74,0.15)' : 'rgba(22,163,74,0.14)') : (bgMode === 'dark' ? 'rgba(3,10,4,0.5)' : 'rgba(0,0,0,0.04)'), border: `1.5px solid ${active ? (bgMode === 'dark' ? 'rgba(34,197,94,0.4)' : 'rgba(22,163,74,0.55)') : (bgMode === 'dark' ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.18)')}`, borderRadius: 12, padding: '12px 14px', color: active ? (bgMode === 'dark' ? '#86efac' : '#15803d') : (bgMode === 'dark' ? 'rgba(140,200,150,0.6)' : 'rgba(30,80,40,0.45)'), fontSize: 13, fontWeight: 600, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}>
                      <span style={{ fontSize: 18 }}>{s.icon}</span>
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </div>

          </div>
        )}

      </div>

      {/* ── GLOBAL MODALS & BANNERS ── */}
      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} />}

      {/* ── VPN BANNER: sticky at bottom of search results ── */}
      {tab === 'search' && results && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 400,
          display: 'flex', justifyContent: 'center',
          padding: `0 16px calc(16px + env(safe-area-inset-bottom, 0px))`,
          pointerEvents: 'none',
        }}>
          <div style={{ width: '100%', maxWidth: 640, pointerEvents: 'all' }}>
            <VpnBanner bgMode={bgMode} />
          </div>
        </div>
      )}

    </div>
  )
}